<script lang="ts" setup="{ ray }">
import type { PropType } from 'vue'
import { getCurrentInstance, onMounted, ref } from 'vue'
import ModalBase from './ModalBase.vue'
import PushButton from './PushButton.vue'
import type { ModalButton, ModalType } from '~/types'

const props = defineProps({
  type: {
    type: String as PropType<ModalType>,
    required: false,
    default: 'info',
  },
  title: {
    type: [Boolean, String],
    required: false,
    default: false,
  },
  body: {
    type: [Boolean, String],
    required: false,
    default: false,
  },
  primary: [Boolean, Object] as PropType<ModalButton>,
  secondary: [Boolean, Object] as PropType<ModalButton>,
  error: String,
})

const vm = getCurrentInstance()
const active = ref(false)
const primaryRef = ref<null | object & { $el: HTMLButtonElement }>(null)
const modalBaseRef = ref<InstanceType<typeof ModalBase> | null>(null)
const isCollapsed = ref(true)

const typeColors: Record<ModalType, string> = {
  success: 'bg-green-100 dark:bg-green-900',
  info: 'bg-blue-100 dark:bg-blue-900',
  danger: 'bg-red-100 dark:bg-red-900',
  warning: 'bg-yellow-100 dark:bg-yellow-900',
}

onMounted(() => {
  active.value = true

  setTimeout(() => {
    if (props.primary && primaryRef && primaryRef.value)
      primaryRef?.value?.$el.focus()
  }, 200)
})

async function action(type: 'primary' | 'secondary') {
  if (modalBaseRef.value)
    modalBaseRef?.value?.destroy()

  if (type === 'primary' && props.primary)
    props.primary.action()
  if (type === 'secondary' && props.secondary)
    props.secondary.action()
}
function removeElement(el: HTMLElement | undefined) {
  if (!el)
    return
  (el.parentNode as HTMLElement).remove()
}
async function destroy() {
  active.value = false
  removeElement(modalBaseRef?.value?.$el)

  if (vm)
    vm.appContext.app.unmount()
}
function copyDetails() {
  if (props.error)
    navigator.clipboard.writeText(props.error)
}
</script>

<template>
  <ModalBase ref="modalBaseRef" :active="active" :destroyed="destroy">
    <div class="sm:flex sm:items-center">
      <div
        :class="typeColors[type]"
        class="mx-auto h-12 w-12 flex flex-shrink-0 items-center self-start justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10"
      >
        <div v-if="type === 'success'" i-mdi-check class="h-5 w-5 text-green-500" />
        <div v-if="type === 'info'" i-mdi-information class="h-5 w-5 text-blue-500" />
        <div v-if="type === 'danger'" i-mdi-close-octagon class="h-5 w-5 text-red-500" />
        <div v-if="type === 'warning'" i-mdi-alert class="h-5 w-5 text-yellow-500" />
      </div>
      <div class="mt-3 w-full sm:ml-4 sm:mt-0 sm:text-left">
        <h3 v-if="title" id="modal-headline" class="mb-1 text-lg text-gray-900 font-medium leading-6 dark:text-gray-300">
          {{ title }}
        </h3>
        <p v-if="body" class="break-word text-sm leading-5 sm:max-w-4xl" v-html="body" />

        <div v-if="error" id="accordion-collapse" class="w-full py-4 pr-12">
          <h2 id="accordion-collapse-heading-1">
            <button type="button" class="w-full flex items-center justify-between border border-gray-200 p-1 text-left text-gray-500 font-medium dark:border-gray-700 hover:bg-gray-100 dark:text-gray-400 focus:ring-4 focus:ring-gray-200 dark:hover:bg-gray-700 dark:focus:ring-gray-700" @click="isCollapsed = !isCollapsed">
              <span>Details</span>
              <div class="flex">
                <button
                  type="button"
                  class="border-1 border-gray-200 p-1 dark:border-gray-700 dark:bg-gray-800"
                  @click.exact.stop.prevent="copyDetails"
                >
                  <div class="m-0 text-gray-500 dark:text-gray-400" i-mdi-clipboard-outline />
                </button>

                <div class="mx-2 h-6 w-6 shrink-0" :class="isCollapsed ? 'i-mdi-chevron-down' : 'i-mdi-chevron-up'" />
              </div>
            </button>
          </h2>
          <div v-if="!isCollapsed">
            <div class="overflow-x-scroll border border-b-0 border-gray-200 p-5 dark:border-gray-700 dark:bg-gray-900">
              <p class="mb-2 break-all text-sm text-gray-500 leading-5 sm:max-w-3xl dark:text-gray-300">
                <pre v-html="error" />
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="primary" class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
      <PushButton
        v-if="primary"
        ref="primaryRef"
        class="max-w-80 w-full flex rounded-md shadow-sm sm:ml-3 sm:w-auto"
        :class="secondary ? '' : 'mr-3'"
        :theme="primary.theme"
        @click="action('primary')"
      >
        {{ primary.label }}
      </PushButton>
      <PushButton
        v-if="secondary"
        class="mt-3 max-w-80 w-full flex rounded-md shadow-sm sm:mt-0 sm:w-auto"
        :theme="secondary.theme"
        @click="action('secondary')"
      >
        {{ secondary.label }}
      </PushButton>
    </div>
  </ModalBase>
</template>
